import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep';
import JsPDF from 'jspdf';
import QRCodeLib from 'qrcode';
import { connect } from 'react-redux';
import { Overlay, Popover } from 'react-bootstrap';
import { parseFullName } from 'parse-full-name';
import { NIL as NIL_UUID } from 'uuid';
import { logger } from '../error-tracker';
import { Segment, dismissForever, dismissTemporarily, isShouldShow } from '../core/core';
import { canUpgradeToClassroom, getNewParentUid, getNewStudentUid, getUsersEmail } from '../GlobalFunctions';
import { isParentsEmailSet } from './UtilityFunctions';
import StudentListOrModeSelector from './StudentListOrModeSelector';
import { SpinnerOverlay } from '../SpinnerOverlay';
import {
  createStudentAccount,
  getQRCodesForStudents,
  getStudentNumberLimits,
  removeStudentAccount,
  updateClassroom as updateClassroomForMode,
  updateDefaultStudentPassword,
  updateStudent,
} from '../BackendInterface';
import { selectClassroom, updateClassroom } from '../Classrooms';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import YesNoDialog from '../components/YesNoDialog/YesNoDialog';
import Spotlight from '../Spotlight';
import { lockWithPreviewUnlessHasAny } from '../lockDecorators';
import MarketingStudents from './MarketingStudents';
import cx from 'classnames';
import styles from '../SCSS/Students.module.scss';
import utilityStyles from '../SCSS/common/utilityStyles.module.scss';
import segmentStyles from '../SCSS/Segment.module.scss';
import type { ClassroomsStateType } from '../Classrooms';
import { History } from 'history';
import { getCookie, setCookie } from '../aaa';
import { AppDispatch, RootState } from '../ApplicationState';
import {
  ClassroomType,
  StudentDBObjectType,
  UserProfileType,
  StudentProxyType,
  AddStudentModeType,
} from '@peekapak/types/DataProtocolTypes';

export type StudentRequestType = {
  childsUserId: string;
  classroomName: string;
  teachersEmail: string;
  classroomId: string;
  webAppBaseUrl: string;
  childsFirstName?: string;
  childsLastName?: string;
  childsMiddleName?: string;
  childsUsername?: string;
  childsPassword?: string;
  parentsPid?: string;
  parentsEmail?: string;
  userProfile?: UserProfileType;
  requestOid?: string;
  ssCode?: string;
};

interface ClassroomsDescriptor {
  state: ClassroomsStateType;
  list: ClassroomType[];
  selectedClassroom: number;
}
interface PrintingParams {
  margin: number;
  maxStudentsPerCol: number;
  maxStudentsPerPage: number;
  leftHorizontalMargin: number;
  rightHorizontalMargin: number;
  horizontalOffset: number;
}
type Props = {
  classrooms: ClassroomsDescriptor;
  classroom: ClassroomType;
  userProfile: UserProfileType;
  updateClassroom: (arg0: ClassroomType) => void;
  history: History;
  isAuthenticated: boolean;
  parentContainer: React.ReactNode;
};
type State = {
  isShowDetailsModal: boolean;
  isEditingParentInvitations: boolean;
  isSendInviteError: boolean;
  isTemporarilyAllowResend: boolean;
  isShowHintConnectParentsHint: boolean;
  isShowHintUnlockHint: boolean;
  isShowSpinner: boolean;
  isShowDeleteConfirmationDialog: boolean;
  isShowClassroomSettingsLink: boolean;
  sendInviteErrorSid: string | void;
  invitationSendPendingList: Array<unknown>;
  defaultStudentPassword: string;
  studentNumberLimit: number | undefined;
  totalNumberOfStudents: number | undefined;
};

class Students extends React.Component<Props, State> {
  #mounted: boolean;
  refToConnectParentsButton: React.RefObject<HTMLButtonElement>;
  refToUnlockMyPeekavilleButton: React.RefObject<HTMLButtonElement>;
  readingLevels: number[];
  indexOfStudentToRemove?: number;
  userIdOfStudentToRemove?: string;
  constructor(props: Props) {
    super(props);
    this.#mounted = false;
    this.refToConnectParentsButton = React.createRef();
    this.refToUnlockMyPeekavilleButton = React.createRef();
    this.state = {
      isShowDetailsModal: false,
      isEditingParentInvitations: false,
      isSendInviteError: false,
      isTemporarilyAllowResend: false,
      isShowHintConnectParentsHint: false,
      isShowHintUnlockHint: false,
      isShowSpinner: false,
      isShowDeleteConfirmationDialog: false,
      isShowClassroomSettingsLink: false,
      sendInviteErrorSid: undefined,
      invitationSendPendingList: [],
      studentNumberLimit: undefined,
      totalNumberOfStudents: undefined,
      defaultStudentPassword: props.userProfile.defaultStudentPassword || '',
    };
    this.readingLevels = [0, 1, 2, 3, 4];
    this.closeModal = this.closeModal.bind(this);
    this.handleOpenInviteParents = this.handleOpenInviteParents.bind(this);
    this.handleSetParentsEmail = this.handleSetParentsEmail.bind(this);
    this.handleOnResetError = this.handleOnResetError.bind(this);
    this.handleTemporarilyAllowResend = this.handleTemporarilyAllowResend.bind(this);
    this.handleOnAddStudents = this.handleOnAddStudents.bind(this);
    this.handleOnDeleteStudent = this.handleOnDeleteStudent.bind(this);
    this.handleOnReplaceStudent = this.handleOnReplaceStudent.bind(this);
    this.hideHint = this.hideHint.bind(this);
    this.hideHintForSession = this.hideHintForSession.bind(this);
    this.hideHintForever = this.hideHintForever.bind(this);
    this.showHint = this.showHint.bind(this);
    this.getHintTargetNode = this.getHintTargetNode.bind(this);
    this.isShouldShowHint = this.isShouldShowHint.bind(this);
    this.handleOnDefaultStudentPasswordChange = this.handleOnDefaultStudentPasswordChange.bind(this);
  }

  handleUpdateAddStudentMode = async (mode: AddStudentModeType) => {
    if (!mode) {
      return;
    }

    this.setState({
      isShowSpinner: true,
    });

    try {
      await updateClassroomForMode(this.props.userProfile.email, this.props.classroom.className, {
        addStudentMode: mode,
      });
      const classroomUnderEdit = cloneDeep(this.props.classroom);
      this.props.updateClassroom({
        ...classroomUnderEdit,
        addStudentMode: mode,
      });
    } catch (error) {
      logger.logException(error as Error);
    } finally {
      this.setState({
        isShowSpinner: false,
      });
    }
  };

  isShouldShowHint(props: Props) {
    const { classroom } = this.props;
    if (!classroom) return false;
    const studentList = classroom.listOfStudentProxies;
    const numberOfParentEmails = this.countParentEmails(studentList);

    if (studentList && numberOfParentEmails < 1 && props.userProfile) {
      return isShouldShow(props.userProfile, 'ConnectParentsHint');
    }

    return false;
  }

  isShouldShowUnlockHint = (props: Props) => {
    if (this.state.isShowHintConnectParentsHint) return false;
    if (this.props.classroom.grade > 5) return false;

    return isShouldShow(props.userProfile, 'UnlockMyPeekavilleHint');
  };

  hideHint(hintName = 'ConnectParentsHint') {
    const newState = {
      isShowSpinner: false,
    };
    newState[`isShowHint${hintName}`] = false;
    this.setState(newState);
  }

  hideHintForever(hintName = 'ConnectParentsHint') {
    this.hideHint(hintName);
    dismissForever(hintName, this.props.userProfile.flags);
  }

  hideHintForSession(hintName = 'ConnectParentsHint') {
    this.hideHint(hintName);
    dismissTemporarily(hintName);
  }

  showHint(hintName = 'ConnectParentsHint') {
    const newState = {};
    newState[`isShowHint${hintName}`] = true;
    this.setState(newState);
  }

  getHintTargetNode(idName: string) {
    const node = document.getElementById(idName);
    return node;
  }

  closeModal(): void {
    this.setState({
      isShowDetailsModal: false,
      isEditingParentInvitations: false,
    });
  }

  handleTemporarilyAllowResend(event: Event) {
    event.preventDefault();
    this.setState({
      isTemporarilyAllowResend: true,
    });
  }

  handleDownloadStudentInfo(event: Event) {
    event.preventDefault();
    const teachersEmail = this.props.userProfile.email;
    const currentClassroom = this.props.classroom;
    const isMiddleSchool = currentClassroom.grade > 5 ? true : false;
    const classroomId = currentClassroom.classroomId;
    const studentList = currentClassroom.listOfStudentProxies;
    const requestList = studentList.map((student) => {
      const newRequest: StudentRequestType = {
        childsUserId: student.userId,
        classroomName: currentClassroom.className,
        teachersEmail,
        classroomId,
        webAppBaseUrl: `${window.location.origin}`,
      };
      if (student.firstName) newRequest.childsFirstName = student.firstName;
      if (student.lastName) newRequest.childsLastName = student.lastName;
      if (student.login) newRequest.childsUsername = student.login;
      if (student.password) newRequest.childsPassword = student.password;
      return newRequest;
    });

    function addVerticalDottedLine(doc: JsPDF) {
      doc.setLineWidth(0.03);

      for (let i = 0; i < 11; i += 0.6) {
        doc.line(4.2, 0.5 + i, 4.2, 0.2 + i);
      }
    }

    const generateFile = async () => {
      try {
        const uuidMap = await getQRCodesForStudents(requestList.map((r) => r.childsUserId));
        const doc = new JsPDF('p', 'in', 'letter');
        addVerticalDottedLine(doc);
        let nextMargin = 0;
        let studentCount = 0;
        for (const request of requestList) {
          request.userProfile = this.props.userProfile;
          nextMargin = await generatePdfPage(doc, request, nextMargin, studentCount, isMiddleSchool, uuidMap);
          studentCount += 1;
        }
        const pdfFileName = `${currentClassroom.className}-StudentLogins.pdf`;
        doc.save(pdfFileName);
      } finally {
        this.setState({
          isShowSpinner: false,
        });
      }
    };

    // Helper function to generate QR code data URL using qrcode library
    async function generateQRCodeDataURL(uuid: string): Promise<string> {
      if (uuid === NIL_UUID) {
        return generateNoQRCodePlaceholder();
      }

      try {
        return await QRCodeLib.toDataURL(uuid, {
          width: 200,
          margin: 1,
        });
      } catch (err) {
        console.error('Error generating QR code:', err);
        return generateNoQRCodePlaceholder();
      }

      function generateNoQRCodePlaceholder(): string {
        const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
          <!-- QR code background pattern -->
          <rect x="40" y="40" width="120" height="120" fill="#f0f0f0" />
          <rect x="50" y="50" width="20" height="20" fill="#d0d0d0" />
          <rect x="130" y="50" width="20" height="20" fill="#d0d0d0" />
          <rect x="50" y="130" width="20" height="20" fill="#d0d0d0" />
          <rect x="80" y="80" width="40" height="40" fill="#d0d0d0" />
          <!-- Strike-through line -->
          <line x1="40" y1="40" x2="160" y2="160" stroke="red" stroke-width="5"/>
          <!-- Border -->
          <rect x="40" y="40" width="120" height="120" fill="none" stroke="#000" stroke-width="2"/>
        </svg>
      `.trim();

        const base64SVG = encodeBase64(svg);
        return `data:image/svg+xml;base64,${base64SVG}`;

        function encodeBase64(str: string): string {
          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
          const bytes = new TextEncoder().encode(str);
          let base64 = '';

          for (let i = 0; i < bytes.length; i += 3) {
            const chunk =
              (bytes[i] << 16) |
              (i + 1 < bytes.length ? bytes[i + 1] << 8 : 0) |
              (i + 2 < bytes.length ? bytes[i + 2] : 0);

            base64 += chars[(chunk >> 18) & 63];
            base64 += chars[(chunk >> 12) & 63];
            base64 += i + 1 < bytes.length ? chars[(chunk >> 6) & 63] : '=';
            base64 += i + 2 < bytes.length ? chars[chunk & 63] : '=';
          }

          return base64;
        }
      }
    }

    async function generatePdfPage(
      doc: JsPDF,
      request: StudentRequestType,
      lastMargin: number,
      studentCount: number,
      isMiddleSchool: boolean,
      uuidMap: Record<string, string>,
    ) {
      const defaultFont = {
        fontSize: 12,
        fontStyle: 'normal',
        fontName: 'Helvetica',
      };

      const titleFont = {
        fontSize: 14,
        fontStyle: 'bold',
        fontName: 'Helvetica',
      };

      const boldFont = {
        fontSize: 12,
        fontStyle: 'bold',
        fontName: 'Helvetica',
      };

      // Generate QR code for student's UUID
      const loginQRCode = uuidMap[request.childsUserId];
      const qrCodeDataUrl = await generateQRCodeDataURL(loginQRCode);

      const paragraphTemplates = [
        {
          text: `<<CHILDS_FIRST_NAME>> <<CHILDS_LAST_NAME>>`,
          font: titleFont,
          spacingAfter: 'single',
        },
        {
          text: `Login: <<CHILDS_USERNAME>>`,
          spacingAfter: 'single',
        },
        {
          text: `Password: <<CHILDS_PASSWORD>>`,
          spacingAfter: 'double',
        },
        {
          text: isMiddleSchool ? 'Login at peekapak.com!' : 'Login or use the QR at \npeekapak.com/login',
          font: boldFont,
          spacingAfter: 'double',
        },
      ];

      function addVerticalDottedLine(doc: JsPDF) {
        doc.setLineWidth(0.03);
        for (let i = 0; i < 11; i += 0.6) {
          doc.line(4.2, 0.5 + i, 4.2, 0.2 + i);
        }
      }

      function addHorizontalDottedLine(doc: JsPDF, height: number) {
        doc.setLineWidth(0.03);
        for (let i = 0; i < 9; i += 0.6) {
          doc.line(0.5 + i, height, 0.2 + i, height);
        }
      }

      const initializeNextLineVerticalOffset = (params: PrintingParams) => {
        const isNewPage = studentCount % params.maxStudentsPerPage === 0;
        const isNewColumn = studentCount % params.maxStudentsPerCol === 0;

        if (isNewPage || isNewColumn) {
          return params.margin;
        }

        return lastMargin + 0.2;
      };

      const setupParams = () => {
        const margin = 0.5;
        const maxStudentsPerCol = 3;
        const maxStudentsPerPage = 6;
        const leftHorizontalMargin = margin * 2 + 0.2;
        const rightHorizontalMargin = 5.5;

        let horizontalOffset;

        // Updated logic for two columns with 3 rows each
        // Switch to right column after 3rd student (index 2)
        if (studentCount % maxStudentsPerPage >= maxStudentsPerCol) {
          horizontalOffset = rightHorizontalMargin;
        } else {
          horizontalOffset = leftHorizontalMargin;
        }

        return {
          margin,
          maxStudentsPerCol,
          maxStudentsPerPage,
          leftHorizontalMargin,
          rightHorizontalMargin,
          horizontalOffset,
        };
      };

      const addPageIfPrintingFullPage = (params: PrintingParams) => {
        const isNewPage = studentCount > 0 && studentCount % params.maxStudentsPerPage === 0;
        if (isNewPage) {
          doc.addPage();
          addVerticalDottedLine(doc);
        }
      };

      const updateNextLineVerticalOffset = (index: number, currentOffset: number, fontSize: number) => {
        if (paragraphTemplates[index].spacingAfter === 'single') {
          return currentOffset + (fontSize / 72) * 1.2;
        } else if (paragraphTemplates[index].spacingAfter === 'double') {
          return currentOffset + (fontSize / 72) * 2.4;
        }
        return currentOffset + (fontSize / 72) * 1.8;
      };

      function mergeData(inputString: string) {
        let newString = inputString;
        const replacementStrings = [
          {
            text: '<<CHILDS_FIRST_NAME>>',
            getNew: (textToFind: string) =>
              request.childsFirstName
                ? newString.replace(textToFind, request.childsFirstName)
                : newString.replace(textToFind, ''),
          },
          {
            text: '<<CHILDS_LAST_NAME>>',
            getNew: (textToFind: string) =>
              request.childsLastName
                ? newString.replace(textToFind, request.childsLastName)
                : newString.replace(textToFind, ''),
          },
          {
            text: '<<CHILDS_USERNAME>>',
            getNew: (textToFind: string) => newString.replace(textToFind, `${request.childsUsername}`),
          },
          {
            text: '<<CHILDS_PASSWORD>>',
            getNew: (textToFind: string) =>
              request.childsPassword
                ? newString.replace(textToFind, `${request.childsPassword}`)
                : newString.replace(textToFind, 'unknown'),
          },
        ];

        for (const replacement of replacementStrings) {
          if (newString.includes(replacement.text)) {
            newString = replacement.getNew(replacement.text);
          }
        }
        return newString;
      }

      const printingParams = setupParams();
      addPageIfPrintingFullPage(printingParams);
      let nextLineVerticalOffset = initializeNextLineVerticalOffset(printingParams);

      const qrCodeWidth = 1; // 1 inch
      const qrCodeHeight = 1; // 1 inch
      const pageHeight = 11; // letter height in inches
      const bottomMargin = printingParams.margin; // 0.5

      // Calculate total space QR code + padding requires
      const qrCodeSpace = qrCodeHeight + 0.5;

      if (nextLineVerticalOffset + qrCodeSpace > pageHeight - bottomMargin) {
        doc.addPage();
        addVerticalDottedLine(doc);
        nextLineVerticalOffset = printingParams.margin;
      }

      // Print text content
      for (let i = 0; i < paragraphTemplates.length; i++) {
        const paragraphTemplate = paragraphTemplates[i];
        const newParagraph = mergeData(paragraphTemplate.text);
        const { fontSize, fontStyle, fontName } = paragraphTemplate.font || defaultFont;

        doc.setFont(fontName, fontStyle);
        doc.setFontSize(fontSize);

        const lines = doc.splitTextToSize(newParagraph, 8.5 - printingParams.margin * 2);
        doc.text(lines, printingParams.horizontalOffset, nextLineVerticalOffset + fontSize / 72);
        nextLineVerticalOffset += lines.length * (fontSize / 72);
        nextLineVerticalOffset = updateNextLineVerticalOffset(i, nextLineVerticalOffset, fontSize);
      }

      if (qrCodeDataUrl.includes('image/svg+xml')) {
        // Convert SVG to PNG first
        const pngDataUrl = await convertSvgToPng(qrCodeDataUrl);
        doc.addImage(
          pngDataUrl,
          'PNG',
          printingParams.horizontalOffset,
          nextLineVerticalOffset,
          qrCodeWidth,
          qrCodeHeight,
        );
      } else {
        // Regular PNG QR code
        doc.addImage(
          qrCodeDataUrl,
          'PNG',
          printingParams.horizontalOffset,
          nextLineVerticalOffset,
          qrCodeWidth,
          qrCodeHeight,
        );
      }
      nextLineVerticalOffset += qrCodeHeight + 0.3;
      // Add horizontal rule after each student except for the last one on the page
      const isLastOnPage = (studentCount + 1) % printingParams.maxStudentsPerPage === 0;
      const isLastStudent = studentCount === requestList.length - 1;

      if (!isLastOnPage && !isLastStudent) {
        addHorizontalDottedLine(doc, nextLineVerticalOffset);
      }

      return nextLineVerticalOffset;

      function convertSvgToPng(svgDataUrl: string): Promise<string> {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          };
          img.onerror = () => reject(new Error('Failed to load SVG'));
          img.src = svgDataUrl;
        });
      }
    }
    this.setState(
      {
        isShowSpinner: true,
      },
      generateFile,
    );
  }

  async handleOnReplaceStudent(
    indexOfStudentToReplace: number,
    newStudentObject: {
      [key: string]: string;
      userId: string;
    },
  ) {
    const oldStudentObject = this.props.classroom.listOfStudentProxies[indexOfStudentToReplace] as Record<
      string,
      unknown
    >;
    const diffArray = Object.entries(newStudentObject).filter((k) => oldStudentObject[k[0]] !== newStudentObject[k[0]]);
    const newData: Record<string, unknown> = {};
    diffArray.forEach(function (data) {
      newData[data[0]] = data[1];
    });
    try {
      await updateStudent(newStudentObject.userId, newData);
    } catch (error) {
      console.error('Student::handleOnReplaceStudent - updateStudent failed: ', error);
      throw error;
    }

    const classroomUnderEdit = cloneDeep(this.props.classroom);
    classroomUnderEdit.listOfStudentProxies[indexOfStudentToReplace] = newStudentObject;
    try {
      const result = await updateClassroomForMode(
        this.props.userProfile.email,
        classroomUnderEdit.className,
        classroomUnderEdit,
      );
      this.props.updateClassroom(classroomUnderEdit);
    } catch (error: any) {
      console.error('Student::handleOnReplaceStudent - postServiceRequest failed: ', error);
      throw error;
    }
  }

  handleOnDeleteStudent(userId: string, event: Event) {
    // stop propagation otherwise the Connect Parents modal
    // will also pop up because this line item has also be clicked upon
    event.stopPropagation();
    this.userIdOfStudentToRemove = userId;
    this.setState({
      isShowDeleteConfirmationDialog: true,
    });
  }

  hideDeleteConfirmationDialog = () =>
    this.setState({
      isShowDeleteConfirmationDialog: false,
    });
  deleteStudent = async () => {
    const myCleanup = () => {
      this.setState({
        isShowSpinner: false,
      });
    };

    this.setState({
      isShowSpinner: true,
      isShowDeleteConfirmationDialog: false,
    });
    const classroomUnderEdit = cloneDeep(this.props.classroom);
    if (this.userIdOfStudentToRemove === undefined) {
      throw new Error('this.userIdOfStudentToRemove is undefined');
    }
    const studentToRemove = classroomUnderEdit.listOfStudentProxies.find(
      (student) => student.userId === this.userIdOfStudentToRemove,
    );
    this.indexOfStudentToRemove = classroomUnderEdit.listOfStudentProxies.findIndex(
      (student) => student.userId === this.userIdOfStudentToRemove,
    );
    classroomUnderEdit.listOfStudentProxies.splice(this.indexOfStudentToRemove, 1);

    if (studentToRemove.userId) {
      // if false then User doesn't have account yet
      try {
        await removeStudentAccount(
          studentToRemove.userId,
          this.props.userProfile.userId, // teachersUserId
          classroomUnderEdit.classroomId,
        );

        const result = updateClassroomForMode(
          this.props.userProfile.email,
          classroomUnderEdit.className,
          classroomUnderEdit,
        );

        this.props.updateClassroom(classroomUnderEdit);
        this.indexOfStudentToRemove = -1;
        myCleanup();
      } catch (error: any) {
        myCleanup();
        console.error('Student::handleOnDeleteStudent - postServiceRequest failed: ', error);
        throw error;
      }
    }
  };

  handleOnDefaultStudentPasswordChange(password: string) {
    this.setState({
      defaultStudentPassword: password,
    });
    this.props.userProfile.defaultStudentPassword = password;
    updateDefaultStudentPassword(
      this.props.userProfile.userId,
      password,
      (_resultObj) => {
        return;
      },
      (errorObj) => {
        console.error(errorObj);
      },
    );
  }

  handleOnAddStudents(
    newStudentNameList: Array<string>,
    newStudentPasswordList: Array<string>,
    newStudentEmailList: Array<string>,
  ) {
    const classroomUnderEdit = cloneDeep(this.props.classroom);

    if (classroomUnderEdit.listOfStudentProxies === undefined) {
      classroomUnderEdit.listOfStudentProxies = [];
    }
    this.setState(
      {
        isShowSpinner: true,
      },
      async () => {
        let itemsProcessed = 0;
        const listOfStudentsToAdd = [];
        let i = 0;
        while (i < newStudentNameList.length) {
          const name = newStudentNameList[i];
          itemsProcessed++;
          const parsedName = parseFullName(name);
          let firstName = parsedName.first;
          let lastName = parsedName.last;
          const middleName = parsedName.middle;
          const password = newStudentPasswordList[i];
          const email = newStudentEmailList[i];
          [firstName, lastName] = fixFirstLastName(firstName, lastName);
          if (!firstName) {
            throw new Error('FirstName is undefined');
          }
          const studentObj: StudentDBObjectType = {
            firstName: firstName,
            classroomId: classroomUnderEdit.classroomId,
            grade: classroomUnderEdit.grade,
            readingLevel: classroomUnderEdit.readingLevel,
            password: password,
            licenseLevel: this.props.userProfile.licenseLevel,
            licenseExpires: this.props.userProfile.licenseExpires,
            createMethod: 'platform',
            teachersUserId: this.props.userProfile.userId,
            culture: this.props.userProfile.culture || '',
            account: this.props.userProfile.account || '',
          };

          if (this.props.userProfile.district) {
            studentObj.district = this.props.userProfile.district;
          }

          if (this.props.userProfile.schoolId) {
            studentObj.schoolId = this.props.userProfile.schoolId;
          }

          if (lastName) studentObj.lastName = lastName;
          if (middleName) studentObj.middleName = middleName;
          if (email && email.trim() !== '') studentObj.email = email.trim();
          try {
            const resultObj = (await createStudentAccount(studentObj)) as StudentProxyType;
            if (resultObj.lastName && resultObj.lastName.length < 1) delete resultObj.lastName;
            resultObj.sid = getNewStudentUid(this.props.userProfile.email, resultObj.className, resultObj.firstName);
            // remove redundant attributes
            const cleanedVersion = removeUnwantedMembers(resultObj, [
              'classroomId',
              'createdAt',
              'createMethod',
              'district',
              'licenseLevel',
              'licenseExpires',
              'modifiedAt',
              'type',
              'username',
              'teachersUserId',
            ]);
            listOfStudentsToAdd.push(cleanedVersion);
          } catch (error) {
            logger.logException(error as Error);
            console.error(error);
          }

          i++;
        }

        const newClassroom = {
          ...this.props.classroom,
          listOfStudentProxies: [...(this.props.classroom?.listOfStudentProxies || []), ...listOfStudentsToAdd],
        };

        try {
          const result = await updateClassroomForMode(
            this.props.userProfile.email,
            newClassroom.className,
            newClassroom,
          );
          this.props.updateClassroom(newClassroom);
          if (itemsProcessed === newStudentNameList.length) {
            this.setState({
              isShowSpinner: false,
              isShowHintUnlockHint:
                this.state.isShowHintUnlockHint && !canUpgradeToClassroom(this.props.userProfile.licenseLevel)
                  ? true
                  : false,
            });
          }
        } catch (error: any) {
          console.error('Student::handleOnAddStudent - postServiceRequest failed: ', error);
          throw error;
        }
      },
    );

    function fixFirstLastName(first: string | undefined, last: string | undefined) {
      if (!first && last) {
        return [last, ''];
      }

      return [first, last];
    }
    this.setState({ isShowHintUnlockHint: false });
  }

  handleOnResetError(event: Event) {
    event.preventDefault();
    this.setState({
      isSendInviteError: false,
      sendInviteErrorSid: undefined,
    });
  }

  handleOpenInviteParents() {
    this.props.history.push('/connectParents');
  }

  async handleSetParentsEmail(studentToEdit: StudentProxyType, parentsEmail: string) {
    const classroomUnderEdit = cloneDeep(this.props.classroom);
    const studentUnderEditIndex = classroomUnderEdit.listOfStudentProxies.findIndex(
      (student) => studentToEdit.sid === student.sid,
    );
    const studentUnderEdit: StudentProxyType = {
      ...classroomUnderEdit.listOfStudentProxies[studentUnderEditIndex],
    };
    let storedParentsEmail = '';

    // does the student even have a list of parents yet?
    // if the stored version doesn't have it, the under edit version
    // won't either
    if (studentUnderEdit.listOfParentProxies === undefined) {
      studentUnderEdit.listOfParentProxies = [];
    }

    if (studentUnderEdit.listOfParentProxies.length === 0) {
      const newParentId = getNewParentUid(studentUnderEdit.sid);
      const newParent = {
        pid: newParentId,
        email: '',
      };
      studentUnderEdit.listOfParentProxies.push(newParent);
    } else {
      if (studentUnderEdit.listOfParentProxies[0].invitationStatus) {
        delete studentUnderEdit.listOfParentProxies[0].invitationStatus;
      }

      storedParentsEmail = studentUnderEdit.listOfParentProxies[0].email;
    }

    if (storedParentsEmail !== parentsEmail) {
      studentUnderEdit.listOfParentProxies[0].email = parentsEmail;
      try {
        const result = await updateClassroomForMode(
          this.props.userProfile.email,
          classroomUnderEdit.className,
          classroomUnderEdit,
        );

        this.props.updateClassroom(classroomUnderEdit);
      } catch (error: any) {
        console.error('Student::handleSetParentsEmail - postServiceRequest failed: ', error);
        throw error;
      }
    }
  }

  removePidFromPendingArray(pid: string) {
    const newPendings = this.state.invitationSendPendingList.slice();
    const index = newPendings.indexOf(pid);

    if (index > -1) {
      newPendings.splice(index, 1);
    }

    return newPendings;
  }

  countUniqueStudents = (classrooms: ClassroomType[]) => {
    const idSet = new Set();
    classrooms.forEach((c: ClassroomType) => {
      if (c.listOfStudentProxies) {
        c.listOfStudentProxies.forEach((s: StudentProxyType) => {
          idSet.add(s.userId);
        });
      }
    });

    return idSet.size;
  };

  setDefaultStudentPasswordByGrade = () => {
    if (this.props.classroom && this.props.classroom.grade < 6) {
      this.setState({
        defaultStudentPassword: this.props.userProfile.defaultStudentPassword || 'ilovepeekapak',
      });
    } else {
      this.setState({
        defaultStudentPassword: this.props.userProfile.defaultStudentPassword || 'PeekapakSEL',
      });
    }
  };

  shouldShowClassroomSettingsLink = () => {
    if (
      this.props.classroom &&
      this.props.classroom.listOfStudentProxies &&
      this.props.classroom.listOfStudentProxies.length > 0
    ) {
      this.setState({
        isShowClassroomSettingsLink: false,
      });
    } else if (getCookie('peekapak.classroomSettingsLink')) {
      this.setState({
        isShowClassroomSettingsLink: false,
      });
    } else {
      this.setState({
        isShowClassroomSettingsLink: true,
      });
    }
  };

  async componentDidMount() {
    this.shouldShowClassroomSettingsLink();
    this.setDefaultStudentPasswordByGrade();

    // will check this flag to see whether state should be updated since
    // we're going to wait for an async call to complete
    this.#mounted = true;
    const map = await getStudentNumberLimits();

    if (!this.#mounted) {
      return;
    }

    const count = this.countUniqueStudents(this.props.classrooms.list);
    this.setState({
      isShowHintConnectParentsHint: this.isShouldShowHint(this.props),
      studentNumberLimit: (map[this.props.userProfile.licenseLevel] as number) || Number.MAX_SAFE_INTEGER,
      totalNumberOfStudents: count,
    });
  }

  componentWillUnmount() {
    this.#mounted = false;
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.classroom !== prevProps.classroom) {
      this.setState({
        isShowHintConnectParentsHint: this.isShouldShowHint(this.props),
      });
      this.setDefaultStudentPasswordByGrade();
    }

    if (this.props.classrooms !== prevProps.classrooms) {
      const count = this.countUniqueStudents(this.props.classrooms.list);
      this.setState({
        totalNumberOfStudents: count,
      });
    }
  }

  countParentEmails(studentList: Array<StudentProxyType>) {
    if (!studentList) {
      return 0;
    }

    let c = 0;
    studentList.forEach((student) => {
      if (isParentsEmailSet(student)) {
        c++;
      }
    });
    return c;
  }

  shouldShowClassCodeInTitle = () =>
    this.props.classroom.addStudentMode &&
    this.props.classroom.addStudentMode === AddStudentModeType.CLASS_CODE &&
    this.props.classroom.listOfStudentProxies &&
    this.props.classroom.listOfStudentProxies.length > 0;
  shouldShowConnectParentsButton = () => !this.shouldShowClassCodeInTitle();
  handleClickClassroomSettings = () => {
    setCookie('peekapak.classroomSettingsLink', 'visited', 1);
  };

  scrollHintIntoViewIfShowing = () => {
    if (
      this.isShouldShowUnlockHint(this.props) &&
      this.state.isShowHintUnlockHint &&
      this.refToUnlockMyPeekavilleButton.current
    ) {
      const rect = this.refToUnlockMyPeekavilleButton.current.getBoundingClientRect();

      if (rect.top < 100) {
        /* preserving for debugging in the future
        console.debug(
          `%cScrolling into view`,
          'background: red; color: black',
          rect
        );
         */
        setTimeout(() => {
          this.refToUnlockMyPeekavilleButton.current?.scrollIntoView();
        }, 0);
      }
    }
  };
  redirectBack = () => {
    this.props.history.goBack();
  };

  render() {
    // console.debug(`%cStudents render`, 'background: blue; color: yellow');
    const UnlockHintPopover = forwardRef((props, ref) => (
      <Popover id={styles.unlockHint} title='Hint' ref={ref as React.RefObject<HTMLDivElement>} {...props}>
        <Popover.Header>Hint</Popover.Header>
        <Popover.Body>
          <div className={styles.hintText}>
            <p>myPeekaville units need to be unlocked before they can be accessed by students.</p>
            <p>
              Click the <span className='boldFace'>Unlock myPeekaville</span> button to choose the unit to unlock.
            </p>
          </div>
          <div className={styles.hintButtons}>
            <PrimaryButton onClick={() => this.hideHintForSession('UnlockHint')} className={styles.popoverButton}>
              Okay
            </PrimaryButton>
            <PrimaryButton onClick={() => this.hideHintForever('UnlockHint')} className={styles.popoverButton}>
              Dismiss
            </PrimaryButton>
          </div>
        </Popover.Body>
      </Popover>
    ));
    UnlockHintPopover.displayName = 'UnlockHintPopover';

    const ConnectParentsHintPopover = React.forwardRef((props, ref) => (
      <Popover id={styles.connectParentsHint} ref={ref as React.RefObject<HTMLDivElement>} {...props}>
        <Popover.Header>Hint</Popover.Header>
        <Popover.Body>
          <div>
            Now that you've added students, connect their parents! Click here to add parent email addresses so they can
            receive <span className='boldFace'>classroom updates, storybooks</span> and{' '}
            <span className='boldFace'>home activities.</span>
          </div>
          <div className={styles.hintButtons}>
            <PrimaryButton onClick={() => this.hideHintForSession()} className={styles.popoverButton} small>
              Okay
            </PrimaryButton>
            &nbsp;
            <PrimaryButton onClick={() => this.hideHintForever()} className={styles.popoverButton} small>
              Dismiss
            </PrimaryButton>
          </div>
        </Popover.Body>
      </Popover>
    ));
    ConnectParentsHintPopover.displayName = 'ConnectParentsHintPopover';

    const ConnectParentsButton = () => (
      <>
        {this.props.classroom.listOfStudentProxies && this.props.classroom.grade < 6 && (
          <div className={styles.oneLineButtons}>
            <PrimaryButton small ref={this.refToConnectParentsButton} onClick={this.handleOpenInviteParents}>
              + Connect Parents
            </PrimaryButton>
          </div>
        )}
      </>
    );

    const UnlockHintOverlay = () => {
      return (
        <>
          {this.isShouldShowUnlockHint(this.props) && this.state.isShowHintUnlockHint && (
            <div className={styles.overlayWrapper}>
              <Overlay
                className={styles.navigatorOverlay}
                onHide={() => this.hideHint('UnlockHint')}
                target={this.refToUnlockMyPeekavilleButton}
                show={true}
                placement='bottom'
              >
                <UnlockHintPopover />
              </Overlay>
              <Spotlight targetName='unlockHint' target={this.refToUnlockMyPeekavilleButton.current} />
            </div>
          )}
        </>
      );
    };

    const ConnectParentsHintOverlay = () => {
      return (
        <>
          {this.state.isShowHintConnectParentsHint && this.refToConnectParentsButton.current && (
            <div className={styles.overlayWrapper}>
              <Overlay
                id='connectParentsOverlay'
                className={styles.navigatorOverlay}
                onHide={() => this.hideHint('ConnectParentsHint')}
                target={this.refToConnectParentsButton}
                show={true}
                placement='bottom'
              >
                <ConnectParentsHintPopover />
              </Overlay>
              <Spotlight targetName='parentshint' target={this.refToConnectParentsButton.current} />
            </div>
          )}
        </>
      );
    };

    if (!this.props.classroom) {
      return null;
    }

    if (this.isLocked()) {
      // trying to access locked content without a pro account
      return <MarketingStudents grade={Number(this.props.classroom.grade)} />;
    }

    const currentClassroom = this.props.classroom;
    const studentList = currentClassroom.listOfStudentProxies;
    this.scrollHintIntoViewIfShowing();
    return (
      <div className={`${styles.Students}`}>
        <SpinnerOverlay isShow={this.state.isShowSpinner} />
        <Segment className={cx(utilityStyles.line, styles.line)}>
          <div className={styles.titleLine}>
            <div className={styles.title}>
              <p className={segmentStyles.moduleTitle}>Students</p>
              {this.state.isShowClassroomSettingsLink && (
                <p>
                  To add students through Google Classroom, Clever or ClassLink please visit the{' '}
                  <Link className={styles.textLink} to='/classroom' onClick={this.handleClickClassroomSettings}>
                    Classroom Settings
                  </Link>{' '}
                  tab.
                </p>
              )}
              {this.shouldShowClassCodeInTitle() && (
                <>
                  <br />
                  <div className={styles.classCodeInTitle}>Class Code: {this.props.classroom.classroomId}</div>
                </>
              )}
            </div>
            {this.shouldShowConnectParentsButton() && <ConnectParentsButton />}
          </div>
        </Segment>
        <YesNoDialog
          isShow={this.state.isShowDeleteConfirmationDialog}
          onHide={this.hideDeleteConfirmationDialog}
          title='Are you sure?'
          onNoResponse={this.hideDeleteConfirmationDialog}
          onYesResponse={this.deleteStudent}
        >
          Removing a student account cannot be undone, and once removed, the account can never be recovered. Are you
          sure you want to do this?
        </YesNoDialog>
        <Segment className={utilityStyles.line}>
          <StudentListOrModeSelector
            studentList={studentList}
            onConnectParents={this.handleOpenInviteParents}
            onAddStudents={this.handleOnAddStudents}
            onDeleteStudent={this.handleOnDeleteStudent}
            onReplaceStudent={this.handleOnReplaceStudent}
            onDownloadStudentInfo={(e: Event) => this.handleDownloadStudentInfo(e)}
            onUpdateAddStudentMode={this.handleUpdateAddStudentMode}
            readingLevels={this.readingLevels}
            defaultStudentPassword={this.state.defaultStudentPassword}
            handleOnDefaultStudentPasswordChange={this.handleOnDefaultStudentPasswordChange}
            showAddNewStudent={!this.shouldShowClassCodeInTitle()}
            totalNumberOfStudents={this.state.totalNumberOfStudents}
            studentNumberLimit={this.state.studentNumberLimit}
            ref={this.refToUnlockMyPeekavilleButton}
          />
        </Segment>
        <div className={styles['spacer-30']} />
        <div className={styles.spacer} />
        <div className={styles.spacer} />
        <UnlockHintOverlay />
        <ConnectParentsHintOverlay />
      </div>
    );
  }
}

export function removeUnwantedMembers<T extends Record<string, unknown>>(
  targetObject: T,
  listOfKeyNames: Array<string>,
): T {
  const copy = { ...targetObject };
  let i = 0;

  while (i < listOfKeyNames.length) {
    if (listOfKeyNames[i] in targetObject) {
      delete copy[listOfKeyNames[i]];
    }

    i += 1;
  }

  return copy;
}

const mapStateToProps = (state: RootState) => ({
  classrooms: {
    state: state.classrooms.state,
    list: state.classrooms.classrooms,
    selectedClassroom: state.classrooms.selectedClassroom,
  },
  classroom: selectClassroom(state),
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateClassroom: (classroom: ClassroomType) => dispatch(updateClassroom(classroom)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(lockWithPreviewUnlessHasAny('KEY_MANAGE_STUDENTS')(Students));
